import { Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/design';
import { HomeDashboard } from '@/domains/admin';
import { useAuth, useCan } from '@/domains/auth';
import { findLabel } from './command-palette';
import { entryTarget, teamNavFor } from './nav';
import { RECORD_KIND_LABEL } from './team-header';
import { useTeamShell } from './team-shell';

/**
 * The Team Portal's home — a working start page rather than a redirect (the two-portals design,
 * §4.1): the palette's search, the viewer's recent records, and every area their permissions open,
 * with one line on what it is for.
 *
 * Under `responses.browse` Home is the team's dashboard instead (`HomeDashboard`, SR-485, SR-486):
 * one read of survey-api's, the same for everyone, which says so when it cannot be read rather than
 * drawing a `0`, with this browser's last six records beside it — no search box or areas, the app
 * bar's search and the rail cover them. Otherwise the areas are the rail's own live entries for this
 * account, and the recent records are this browser's. What the Team Portal is still to have is listed under the
 * areas by name, as the rail tags it — not drawn. With the demo on, an entry the demo draws a mock
 * of is no longer listed as coming.
 *
 * **What the account opens decides what is drawn** (SR-228): the dashboard only under
 * `responses.browse`, the work items' own read. A Team Portal account that opens no area at all
 * gets one sentence and no search, tiles or counts — there is nothing on them it could follow: one
 * given no role yet is told so and who gives roles (the role-model UI design's §5); one whose roles
 * open nothing here is told that instead, since "no role" would be false. The search box's words
 * are the palette's own (`findLabel`), naming only what the account can search.
 */

/** One line per live area, in the words its screen would use. */
const PURPOSE: Record<string, string> = {
  'My tasks':
    'Everything assigned to you — compliance work items, DD calls, reinstatements and Lists rows.',
  'Compliance queues':
    'Every work item by survey queue — what raised it, who has it, when it is due — with its decisions.',
  'Number checks':
    'Every number registration could not confirm — why, and who has it — to verify, reject or replace.',
  People:
    'Every person with their teams and firms, numbers, surveys and account — found by a name, a firm, an address or any number.',
  Teams: 'Every team with its lead, members, routing, account and latest response.',
  Firms:
    'The RIA firms and organisations we hold — their numbers, principals, SEC ADV match and latest response.',
  'SEC ADV register':
    'The investment advisers the SEC lists, as last imported, with each firm’s filing.',
  Responses:
    'Every response in every cycle, read as its questionnaire, with its timeline, review flags and notes.',
  Registrations:
    'Every self-registration attempt, the ones that ended without an account included.',
  'Survey builder': 'The question bank, the option lists and every survey version, with the dry run and publish.',
  Cycles: 'Which published version each survey’s ranking cycle shows respondents.',
  'Users & roles': 'Who can sign in, their Team Portal roles, and what each role allows.',
  'Concern criteria': 'What raises a work item from a BrokerCheck answer, every version kept.',
  'Escalation levels': 'Who an escalated work item goes to, level by level.'
};

export function TeamHome() {
  const { user } = useAuth();
  const can = useCan();
  const { openPalette, recent } = useTeamShell();
  const groups = teamNavFor(can);

  if (groups.length === 0) {
    return (
      <>
        <PageHeader title="Home" />
        <p className="max-w-3xl px-6 py-6">
          {user && user.roles.length > 0 ? (
            'Your Team Portal roles open no section here yet.'
          ) : (
            <>
              Your account has no Team Portal role yet. An administrator gives roles on Setup →
              Users &amp; roles.
            </>
          )}
        </p>
      </>
    );
  }

  const live = groups.flatMap((group) =>
    group.entries.filter((entry) => entry.to).map((entry) => ({ ...entry, group: group.label }))
  );
  const coming = groups.flatMap((group) => group.entries.filter((entry) => !entryTarget(entry)));

  const search = (
    <button
      type="button"
      onClick={openPalette}
      className="flex h-11 w-full max-w-2xl items-center gap-3 rounded-lg border border-[var(--veritant-royal-55)] bg-white px-4 text-left text-[0.9375rem] text-muted-foreground shadow-xs outline-none hover:border-[var(--color-royal-blue)] focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Search className="size-5 shrink-0" aria-hidden />
      {findLabel(can)}
      <kbd className="ml-auto rounded border border-border px-1.5 font-sans text-xs">Ctrl K</kbd>
    </button>
  );

  const areas = (
    <div className="grid content-start gap-6">
      <section aria-labelledby="team-home-areas" className="grid gap-3">
        <h2 id="team-home-areas" className="text-base font-medium text-[var(--color-royal-blue)]">
          Areas
        </h2>
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {live.map((entry) => {
            const Icon = entry.icon;
            return (
              <li key={entry.label}>
                <Link
                  to={entry.to!}
                  className="group flex h-full gap-3 rounded-lg border border-border bg-white p-4 outline-none hover:border-[var(--color-teal)] focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--color-turquoise)_12%,white)] text-[var(--color-teal)]">
                    <Icon className="size-4.5" aria-hidden />
                  </span>
                  <span className="grid gap-0.5">
                    <span className="text-[0.6875rem] text-muted-foreground">{entry.group}</span>
                    <span className="font-medium text-[var(--color-royal-blue)] group-hover:underline">
                      {entry.label}
                    </span>
                    <span className="text-[0.8125rem] leading-snug text-muted-foreground">
                      {PURPOSE[entry.label]}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      {coming.length > 0 && (
        <section aria-labelledby="team-home-coming" className="grid gap-2">
          <h2 id="team-home-coming" className="text-base font-medium text-[var(--color-royal-blue)]">
            Coming to the Team Portal
          </h2>
          <p className="max-w-3xl text-muted-foreground">
            {coming.map((entry) => entry.label).join(', ')}. Each is on the rail, tagged
            "coming", until it has a screen.
          </p>
        </section>
      )}
    </div>
  );

  const recentRecords = (
    <aside aria-labelledby="team-home-recent" className="grid content-start gap-3">
      <h2 id="team-home-recent" className="text-base font-medium text-[var(--color-royal-blue)]">
        Recent records
      </h2>
      {recent.length === 0 ? (
        <p className="text-muted-foreground">
          Nothing opened yet on this browser. A person, a firm, a response or an account you
          open is kept here.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-white">
          {recent.map((record) => (
            <li key={record.path}>
              <Link
                to={record.path}
                className="grid gap-0.5 px-4 py-2.5 outline-none hover:bg-[color-mix(in_srgb,var(--color-turquoise)_8%,white)] focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="truncate font-medium text-[var(--color-royal-blue)]">
                  {record.label}
                </span>
                <span className="text-[0.6875rem] text-muted-foreground">
                  {RECORD_KIND_LABEL[record.kind]}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );

  if (can('responses.browse')) {
    return (
      <HomeDashboard
        recent={
          recent.length === 0 ? (
            <p className="px-4 pb-3 text-[0.8125rem] text-muted-foreground">Nothing opened yet on this browser.</p>
          ) : (
            <ul className="grid px-4 pb-2 text-[0.8125rem]">
              {recent.slice(0, 6).map((record) => (
                <li key={record.path} className="border-t border-border first:border-t-0">
                  <Link
                    to={record.path}
                    className="flex items-baseline gap-2 py-0.5 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="min-w-0 truncate font-medium text-[var(--color-royal-blue)]">{record.label}</span>
                    <span className="ml-auto shrink-0 text-[0.6875rem] text-muted-foreground">
                      {RECORD_KIND_LABEL[record.kind]}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Home"
        lede="Find anyone in one box, pick up a record you opened, or go straight to an area."
      />

      <div className="grid gap-6 px-6 py-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="grid content-start gap-6">
          {search}
          {areas}
        </div>
        {recentRecords}
      </div>
    </>
  );
}
